/**
 * InquiriesPage - List studio's submitted inquiries
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getMyInquiries,
  submitInquiry,
  submitBookingQuestion,
  formatInquirySource,
  formatInquiryStatus,
  getStatusColor,
  type StudioInquiry,
} from '../../api/inquiries';
import { getBookings, type BookingListItem } from '../../api/studios';
import { getShows, type Show } from '../../api/events';
import { DashboardMobileNav } from '../../components/dashboard';

type FilterStatus = 'all' | 'pending' | 'in_review' | 'responded' | 'converted' | 'closed';

export default function InquiriesPage() {
  const { user, logout } = useAuth();
  const [inquiries, setInquiries] = useState<StudioInquiry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [showBookingQuestionModal, setShowBookingQuestionModal] = useState(false);
  const [bookingsList, setBookingsList] = useState<BookingListItem[]>([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [bookingQuestionText, setBookingQuestionText] = useState('');
  const [isSubmittingBookingQuestion, setIsSubmittingBookingQuestion] = useState(false);
  const [bookingQuestionSuccess, setBookingQuestionSuccess] = useState(false);

  // Submit Question modal state
  const [showSubmitQuestionModal, setShowSubmitQuestionModal] = useState(false);
  const [showsList, setShowsList] = useState<Show[]>([]);
  const [questionShowId, setQuestionShowId] = useState('');
  const [questionShowName, setQuestionShowName] = useState('');
  const [questionText, setQuestionText] = useState('');
  const [isSubmittingQuestion, setIsSubmittingQuestion] = useState(false);
  const [questionSuccess, setQuestionSuccess] = useState(false);

  useEffect(() => {
    loadInquiries();
    getShows().then(setShowsList).catch(() => {
      // silently fail — modal works without show options
    });
  }, []);

  const loadInquiries = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getMyInquiries();
      setInquiries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inquiries');
    } finally {
      setIsLoading(false);
    }
  };

  const loadBookingsForModal = async () => {
    try {
      const result = await getBookings({ limit: 50 });
      setBookingsList(result.data);
    } catch {
      // silently fail - modal will show empty
    }
  };

  const handleBookingQuestionSubmit = async () => {
    if (!selectedBookingId || !bookingQuestionText.trim()) return;
    setIsSubmittingBookingQuestion(true);
    try {
      await submitBookingQuestion({ bookingId: selectedBookingId, message: bookingQuestionText.trim() });
      setShowBookingQuestionModal(false);
      setSelectedBookingId('');
      setBookingQuestionText('');
      setBookingQuestionSuccess(true);
      setTimeout(() => setBookingQuestionSuccess(false), 3000);
      loadInquiries();
    } catch {
      // handle error
    } finally {
      setIsSubmittingBookingQuestion(false);
    }
  };

  const openBookingQuestionModal = () => {
    loadBookingsForModal();
    setShowBookingQuestionModal(true);
  };

  const handleSubmitQuestion = async () => {
    if (!questionText.trim()) return;
    setIsSubmittingQuestion(true);
    try {
      await submitInquiry({
        source: 'general',
        ...(questionShowId ? { showId: questionShowId, showName: questionShowName } : {}),
        message: questionText.trim(),
      });
      setShowSubmitQuestionModal(false);
      setQuestionShowId('');
      setQuestionShowName('');
      setQuestionText('');
      setQuestionSuccess(true);
      setTimeout(() => setQuestionSuccess(false), 4000);
      loadInquiries();
    } catch {
      // silently fail — could add error state here
    } finally {
      setIsSubmittingQuestion(false);
    }
  };

  const filteredInquiries = inquiries.filter((inq) => {
    if (filterStatus === 'all') return inq.status !== 'closed';
    return inq.status === filterStatus;
  });

  const statusCounts = {
    all: inquiries.filter((i) => i.status !== 'closed').length,
    pending: inquiries.filter((i) => i.status === 'pending').length,
    in_review: inquiries.filter((i) => i.status === 'in_review').length,
    responded: inquiries.filter((i) => i.status === 'responded').length,
    converted: inquiries.filter((i) => i.status === 'converted').length,
    closed: inquiries.filter((i) => i.status === 'closed').length,
  };

  return (
    <div className="min-h-screen bg-warm-50">
      {/* Header */}
      <header className="bg-white border-b border-warm-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/logo.png" alt="Stage Door Access" className="h-7" />
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-warm-600 hover:text-warm-900 text-sm">
                Dashboard
              </Link>
              <Link to="/dashboard/bookings" className="text-warm-600 hover:text-warm-900 text-sm">
                My Bookings
              </Link>
              <Link to="/dashboard/inquiries" className="text-primary-600 font-medium text-sm">
                My Inquiries
              </Link>
              <Link to="/dashboard/profile" className="text-warm-600 hover:text-warm-900 text-sm">
                Studio Profile
              </Link>
              <Link to="/dashboard/contacts" className="text-warm-600 hover:text-warm-900 text-sm">
                Contacts
              </Link>
            </nav>

            <div className="flex items-center gap-4">
              <span className="text-warm-600 text-sm hidden sm:block">{user?.email}</span>
              <button
                onClick={() => logout()}
                className="text-warm-600 hover:text-warm-900 text-sm font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>
      <DashboardMobileNav />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="hidden sm:block mb-6">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <Link to="/dashboard" className="text-primary-600 hover:text-primary-700">
                Dashboard
              </Link>
            </li>
            <li className="text-warm-400">/</li>
            <li className="text-warm-600">My Inquiries</li>
          </ol>
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900 mb-2">My Inquiries</h1>
            <p className="hidden sm:block text-warm-600">
              Track your inquiry requests and their responses.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={openBookingQuestionModal}
              className="px-4 py-2 border border-warm-300 rounded-lg text-warm-700 hover:bg-warm-50 font-medium whitespace-nowrap flex items-center gap-2 text-sm"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="hidden sm:inline">Ask About a Booking</span>
            </button>
            <button
              onClick={() => setShowSubmitQuestionModal(true)}
              className="btn-primary whitespace-nowrap flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="hidden sm:inline">Submit Question</span>
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-danger-700">{error}</span>
            <button
              onClick={loadInquiries}
              className="ml-auto text-sm font-medium text-danger-600 hover:text-danger-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Submit Question Success */}
        {questionSuccess && (
          <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg text-success-700 text-sm">
            Your question has been submitted! We'll respond within 24 hours.
          </div>
        )}

        {/* Booking Question Success */}
        {bookingQuestionSuccess && (
          <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg text-success-700 text-sm">
            Your question has been submitted! We'll respond within 24 hours.
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-5 sm:mb-6 overflow-x-auto pb-1">
          {(
            [
              { value: 'all', label: 'Active' },
              { value: 'pending', label: 'Pending' },
              { value: 'in_review', label: 'In Review' },
              { value: 'responded', label: 'Responded' },
              { value: 'converted', label: 'Converted' },
              { value: 'closed', label: 'Closed' },
            ] as { value: FilterStatus; label: string }[]
          ).map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilterStatus(tab.value)}
              className={`flex-shrink-0 whitespace-nowrap px-3 py-1.5 sm:px-4 sm:py-2 rounded-full sm:rounded-lg text-sm font-medium transition-colors ${
                filterStatus === tab.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-warm-600 hover:bg-warm-100 border border-warm-200'
              }`}
            >
              {tab.label}
              <span className={`ml-1.5 text-xs tabular-nums ${
                filterStatus === tab.value ? 'text-white/70' : 'text-warm-400'
              }`}>
                {statusCounts[tab.value]}
              </span>
            </button>
          ))}
        </div>

        {/* Inquiries List */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card animate-pulse">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="h-12 w-12 bg-warm-100 rounded-lg" />
                  <div className="flex-1 space-y-3">
                    <div className="h-5 bg-warm-100 rounded w-1/3" />
                    <div className="h-4 bg-warm-100 rounded w-1/2" />
                  </div>
                  <div className="h-8 bg-warm-100 rounded w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredInquiries.length === 0 ? (
          <div className="card text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-warm-900 mb-2">
              {filterStatus === 'all' ? 'No inquiries yet' : `No ${filterStatus.replace('_', ' ')} inquiries`}
            </h3>
            <p className="text-warm-600 mb-6">
              {filterStatus === 'all'
                ? "You haven't submitted any inquiries yet. Submit a question to get started!"
                : `You don't have any inquiries with this status.`}
            </p>
            {filterStatus === 'all' && (
              <button className="btn-primary" onClick={() => setShowSubmitQuestionModal(true)}>
                Submit Question
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredInquiries.map((inquiry) => (
              <InquiryCard key={inquiry.id} inquiry={inquiry} />
            ))}
          </div>
        )}
        {/* Submit Question Modal */}
        {showSubmitQuestionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-warm-900 mb-4">Submit a Question</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">Related Show (optional)</label>
                  <select
                    value={questionShowId}
                    onChange={(e) => {
                      const selected = showsList.find(s => s.id === e.target.value);
                      setQuestionShowId(e.target.value);
                      setQuestionShowName(selected?.name ?? '');
                    }}
                    className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  >
                    <option value="">General — not about a specific show</option>
                    {showsList.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">Your Question</label>
                  <textarea
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    placeholder="Type your question here..."
                    maxLength={2000}
                    rows={4}
                    className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
                  />
                  <p className="text-xs text-warm-400 mt-1 text-right">{questionText.length}/2000</p>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setShowSubmitQuestionModal(false); setQuestionShowId(''); setQuestionShowName(''); setQuestionText(''); }}
                  className="flex-1 px-4 py-2 border border-warm-300 rounded-lg text-warm-700 hover:bg-warm-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitQuestion}
                  disabled={!questionText.trim() || isSubmittingQuestion}
                  className="flex-1 btn-primary text-sm disabled:opacity-50"
                >
                  {isSubmittingQuestion ? 'Submitting...' : 'Submit Question'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Booking Question Modal */}
        {showBookingQuestionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-warm-900 mb-4">Ask About a Booking</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">Select Booking</label>
                  <select
                    value={selectedBookingId}
                    onChange={(e) => setSelectedBookingId(e.target.value)}
                    className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  >
                    <option value="">Choose a booking...</option>
                    {bookingsList.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.show.name} — {b.venue.name}{b.venue.city ? `, ${b.venue.city}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">Your Question</label>
                  <textarea
                    value={bookingQuestionText}
                    onChange={(e) => setBookingQuestionText(e.target.value)}
                    placeholder="Type your question here..."
                    maxLength={2000}
                    rows={4}
                    className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
                  />
                  <p className="text-xs text-warm-400 mt-1 text-right">{bookingQuestionText.length}/2000</p>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setShowBookingQuestionModal(false); setSelectedBookingId(''); setBookingQuestionText(''); }}
                  className="flex-1 px-4 py-2 border border-warm-300 rounded-lg text-warm-700 hover:bg-warm-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBookingQuestionSubmit}
                  disabled={!selectedBookingId || !bookingQuestionText.trim() || isSubmittingBookingQuestion}
                  className="flex-1 btn-primary text-sm disabled:opacity-50"
                >
                  {isSubmittingBookingQuestion ? 'Submitting...' : 'Submit Question'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function InquiryCard({ inquiry }: { inquiry: StudioInquiry }) {
  const statusColors = getStatusColor(inquiry.status);
  const createdDate = new Date(inquiry.createdAt);

  return (
    <Link
      to={`/inquiry/status/${inquiry.token}`}
      className="card block hover:shadow-md transition-shadow group"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Show Info */}
        <div className="flex items-center gap-4 flex-1">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
            {inquiry.showLogoUrl ? (
              <img
                src={inquiry.showLogoUrl}
                alt={inquiry.showName ?? 'General Inquiry'}
                className="w-full h-full object-cover rounded-lg"
              />
            ) : (
              <span className="text-primary-500 text-lg font-display font-bold">
                {(inquiry.showName ?? 'G').charAt(0)}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-warm-900 group-hover:text-primary-600 transition-colors truncate">
              {inquiry.showName ?? 'General Inquiry'}
            </h3>
            <p className="text-sm text-warm-600">
              {formatInquirySource(inquiry.source)}
              {inquiry.venueName && ` · ${inquiry.venueName}`}
            </p>
            <p className="text-sm text-warm-500">
              Submitted {createdDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>

        {/* Status */}
        <div className="flex items-center gap-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors}`}>
            {formatInquiryStatus(inquiry.status)}
          </span>

          <svg
            className="w-5 h-5 text-warm-400 group-hover:text-primary-600 transition-colors hidden sm:block"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  );
}
